import { useState, useEffect, useCallback } from 'react';
import { Permissions } from '../interfaces/IPermissions';
import { API_BASE } from '../../../config/constants';

/**
 * Custom React hook to fetch and manage permissions view data.
 * Handles loading state and provides permissions data array.
 *
 * @returns {{ permissions: Permissions[]; loading: boolean }}
 */

export function usePermissions(userId?: string | number | null) {
    const [permissions, setPermissions] = useState<Permissions[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const loadPermissions = useCallback(async () => {
        if (userId === null || userId === undefined || String(userId).trim() === '') {
            setPermissions([]);
            return;
        }

        setLoading(true);
        // console.debug('[usePermissions] loadPermissions called', { userId });

        try {
            const response = await fetch(`${API_BASE}/api/users/get-all-user-permissions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_id: userId }),
            });

            const data = await response.json();
            if (data && Array.isArray(data.data)) {
                setPermissions(data.data);
            } else if (Array.isArray(data?.permissions)) {
                setPermissions(data.permissions);
            } else if (Array.isArray(data)) {
                setPermissions(data);
            } else {
                setPermissions([]);
            }
        } catch (error) {
            console.error('usePermissions: Error fetching permissions data:', error);
            setPermissions([]);
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        loadPermissions();
    }, [loadPermissions]);

    const createPermission = useCallback(
        async (targetUserId: string | number, objectId: string | number, roleId: string | number, nda?: boolean): Promise<boolean> => {
            try {
                console.debug('[usePermissions] createPermission called', { targetUserId, objectId, roleId, nda });
                const response = await fetch(`${API_BASE}/api/objects/create-permission`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        user_id: targetUserId,
                        object_id: objectId,
                        role_id: roleId,
                        nda: nda ? true : false,
                    }),
                });

                const result = await response.json().catch(() => ({}));
                console.debug('[usePermissions] createPermission response', { ok: response.ok, success: result?.success });
                if (!response.ok || !result?.success) {
                    return false;
                }

                await loadPermissions();
                return true;
            } catch (error) {
                console.error('usePermissions: Error creating permission:', error);
                return false;
            }
        },
        [loadPermissions]
    );

    const removePermission = useCallback(
        async (targetUserId: string | number, objectId: string | number | null): Promise<boolean> => {
            try {
                const response = await fetch(`${API_BASE}/api/objects/remove-permission`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        user_id: targetUserId,
                        object_id: objectId,
                    }),
                });

                const result = await response.json();
                if (!response.ok || !result?.success) {
                    return false;
                }

                await loadPermissions();
                return true;
            } catch (error) {
                console.error('usePermissions: Error removing permission:', error);
                return false;
            }
        },
        [loadPermissions]
    );

    return { permissions, loading, loadPermissions, createPermission, removePermission };
}
